import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import { ServicemeshConfigService } from '@helpers/services/config';
import { fromPairs } from 'lodash-es';
import { map, Observable, ReplaySubject, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ServicemeshMediaWatcherService {
  private readonly _breakpointObserver = inject(BreakpointObserver);
  private readonly _servicemeshConfigService = inject(ServicemeshConfigService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _onMediaChange: ReplaySubject<{ matchingAliases: string[]; matchingQueries: any }> =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new ReplaySubject<{ matchingAliases: string[]; matchingQueries: any }>(1);

  /**
   * Constructor
   */
  constructor() {
    this._servicemeshConfigService.config$
      .pipe(
        map((config) =>
          fromPairs(
            Object.entries(config.screens).map(([alias, screen]) => [
              alias,
              `(min-width: ${screen})`,
            ])
          )
        ),
        switchMap((screens) =>
          this._breakpointObserver.observe(Object.values(screens)).pipe(
            map((state) => {
              // Prepare the observable values and set their defaults
              const matchingAliases: string[] = [];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const matchingQueries: any = {};

              // Get the matching breakpoints and use them to fill the subject
              const matchingBreakpoints =
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                Object.entries(state.breakpoints).filter(([_query, matches]) => matches) ?? [];
              for (const [query] of matchingBreakpoints) {
                // Find the alias of the matching query
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const matchingAlias = Object.entries(screens).find(([_alias, q]) => q === query)?.[0];

                // Add the matching query to the observable values
                if (matchingAlias) {
                  matchingAliases.push(matchingAlias);
                  matchingQueries[matchingAlias] = query;
                }
              }

              // Execute the observable
              this._onMediaChange.next({
                matchingAliases,
                matchingQueries,
              });
            })
          )
        )
      )
      .subscribe();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for _onMediaChange
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get onMediaChange$(): Observable<{ matchingAliases: string[]; matchingQueries: any }> {
    return this._onMediaChange.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * On media query change
   *
   * @param query
   */
  onMediaQueryChange$(query: string | string[]): Observable<BreakpointState> {
    return this._breakpointObserver.observe(query);
  }
}
